import { Reveal } from "@/components/common";
import { IMAGES, MOCKUP_PHOTOS } from "@/lib/data";
import { useI18n } from "@/i18n";
import { Play, Download, Upload, Sparkles, Film, Smartphone, Monitor } from "lucide-react";

function StepVisual({ i }) {
  const { t } = useI18n();
  const m = (k) => t(`workflow.mock.${k}`);
  if (i === 0) {
    return (
      <div>
        <div className="mb-2 flex items-center justify-between text-[11px]">
          <span className="font-semibold text-re-ink">Villa Zeezicht 12</span>
          <span className="text-re-muted">{m("photos").replace("{n}", "14")}</span>
        </div>
        <div className="mb-2 flex items-center gap-1.5 rounded-md border border-dashed border-re-line bg-white px-2 py-1.5 text-[11px] text-re-muted">
          <Upload className="h-3 w-3 text-re-red" /> {m("drop")}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MOCKUP_PHOTOS.slice(0, 6).map((src, k) => (
            <div key={k} className="aspect-square overflow-hidden rounded-md ring-1 ring-re-line">
              <img src={src} alt="Geüploade vastgoedfoto" loading="lazy" className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (i === 1) {
    return (
      <div>
        <div className="mb-2 flex items-center justify-between text-[11px]">
          <span className="font-semibold text-re-ink">{m("format")}</span>
          <span className="text-re-muted">Villa Zeezicht 12</span>
        </div>
        <div className="mb-2 flex gap-1.5">
          <span className="flex items-center gap-1 rounded-full bg-gradient-red px-2.5 py-1 text-[10px] font-semibold text-white">
            <Smartphone className="h-3 w-3" /> {m("vertical")}
          </span>
          <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-medium text-re-charcoal ring-1 ring-re-line">
            <Monitor className="h-3 w-3 text-re-red" /> {m("horizontal")}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MOCKUP_PHOTOS.slice(0, 6).map((src, k) => (
            <div key={k} className="aspect-square overflow-hidden rounded-md ring-1 ring-re-line">
              <img src={src} alt="Geüploade vastgoedfoto" loading="lazy" className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
        <div className="mt-2 flex">
          <span className="ml-auto flex items-center gap-1 rounded-full bg-gradient-red px-3 py-1 text-[10px] font-semibold text-white">
            <Sparkles className="h-3 w-3" /> {m("generate")}
          </span>
        </div>
      </div>
    );
  }
  // Step 3 — six video options + download actions
  const thumbs = [IMAGES.villaHero, IMAGES.living1, IMAGES.kitchen1, IMAGES.living2, IMAGES.villaPool, IMAGES.kitchen2];
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold text-re-ink">{m("options")}</span>
        <span className="text-re-muted">{m("selected")}</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {thumbs.map((src, k) => (
          <div key={k} className={`relative aspect-video overflow-hidden rounded-md ${k === 0 ? "ring-2 ring-re-red" : "ring-1 ring-re-line"}`}>
            <img src={src} alt={`Video-optie ${k + 1}`} loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute inset-0 grid place-items-center">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/90"><Play className="ml-0.5 h-2.5 w-2.5 fill-re-ink text-re-ink" /></span>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <span className="flex items-center justify-center gap-1 rounded-md bg-gradient-red px-2 py-1.5 text-[10px] font-semibold text-white">
          <Download className="h-3 w-3" /> {m("download")}
        </span>
        <span className="flex items-center justify-center gap-1 rounded-md bg-white px-2 py-1.5 text-[10px] font-semibold text-re-charcoal ring-1 ring-re-line">
          <Film className="h-3 w-3 text-re-red" /> {m("clips")}
        </span>
      </div>
    </div>
  );
}

export function Workflow() {
  const { t } = useI18n();
  const steps = t("workflow.steps");
  return (
    <section id="zo-werkt-het" className="overflow-visible bg-re-soft/60 py-24 md:py-32" data-testid="workflow">
      <div className="container-re">
        <Reveal className="mx-auto max-w-2xl px-2 text-center">
          <h2 className="pb-1 font-heading text-3xl font-black leading-[1.12] tracking-tight text-re-ink md:text-5xl">
            {t("workflow.title")} <span className="text-gradient-red">{t("workflow.titleAccent")}</span>.
          </h2>
          <p className="mt-5 text-base text-re-muted md:text-lg">
            {t("workflow.sub")}
          </p>
        </Reveal>

        <div className="relative mt-16 grid gap-6 md:grid-cols-3 md:gap-8">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="group relative flex h-full flex-col rounded-3xl border border-re-line bg-white p-7 transition-all hover:-translate-y-1.5 hover:border-re-red/25 hover:shadow-premium">
                <div className="rounded-2xl border border-re-line bg-gradient-to-b from-re-soft/70 to-white p-4">
                  <StepVisual i={i} />
                </div>
                <div className="mt-6 flex items-center gap-3">
                  <span className="font-heading text-4xl font-black leading-none text-transparent [-webkit-text-stroke:1.5px_#E5E5E7] group-hover:[-webkit-text-stroke:1.5px_#F0182D]">
                    {i + 1}
                  </span>
                  <h3 className="font-heading text-xl font-bold text-re-ink">{s.title}</h3>
                </div>
                <p className="mt-2 text-[15px] leading-relaxed text-re-muted">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
